import { useCtxStore } from '@/stores/ctx.store'
import { useFsdStore } from '@/stores/fsd.store'
import consola from 'consola'
import moment from 'moment'
import { defineStore } from 'pinia'
import { firstValueFrom } from 'rxjs'
import type { Subscription } from 'rxjs'
import { getActualsUrlParamStr, getDateStr, getRangeUrlParamStr, verifyAndGetActualsUrlParam, verifyAndGetRangeUrlParam } from '~/composables/commons'
import { CustomDateRangeStrategy, type DateRangeStrategy, YearToDateStrategy } from '~/composables/date-range-strategies'
import { getCompEndMonth } from '~/composables/financials-grid-commons'
import { useAppSettings } from '~/composables/useAppSettings'
import { useKpiLoader } from '~/composables/useKpiLoader'
import { useAcmStore } from '~/stores/acm.store'
import type { FinancialsGridDataAvailablePlanList } from '~/types/stcs_fpna_res_values.entity'
import type { ComparisionViewProps, SectionKey } from '~/types/stcs_wapp_ctx_data.entity'
import { type Ref, computed, ref, watch, watchEffect } from '#imports'
import { FinancialGridDataView } from './grid-data.types'

/**
 * Store for shared data like selected plan, last actuals date, and related UI logic.
 */
export const useSharedScopeStore = defineStore('shared-scope', () => {
  const route = useRoute()
  const router = useRouter()

  const selectedPlan = ref<Record<string, FinancialsGridDataAvailablePlanList | undefined>>({})
  const lastActualsDate = ref<Record<string, string>>({})
  const userHasSetActualsDate = ref<Record<string, boolean>>({})

  const minDate = ref(new Date())
  const maxDate = ref(new Date())
  const selDateRangeStrategy = ref<DateRangeStrategy | undefined>(undefined)
  const planWarning = ref<Record<string, boolean>>({})

  const ctxStore = useCtxStore()
  const fsdStore = useFsdStore()
  const { updateSelectedPlanId, updateLastActualsDate } = useAppSettings()

  const activeOrgId = computed(() => ctxStore.active_org_id)
  const activeCompId = computed(() => ctxStore.active_comp_id)
  const isDashboardRoute = computed(() => router.currentRoute.value.path.includes('/dashboard/'))

  const hasPlan = (compId: string): boolean => Boolean(selectedPlan.value[compId]?.id)
  const getSelectPlanId = (compId: string): string => selectedPlan.value[compId]?.id ?? ''

  const availablePlans = computed(() => {
    if (!activeCompId.value) return []
    return ctxStore.ref_comp_map[activeCompId.value]?.availablePlanList || []
  })

  const financialsDiff = computed<ComparisionViewProps | undefined>(() => {
    if (!activeCompId.value) return {}
    return ctxStore.ref_comp_map[activeCompId.value].ui_map.financials.comparision_view || {}
  })

  /**
   * Checks if the plan start date is after the given date range end.
   * This indicates that plan data would be unavailable for the selected period.
   * @param planStartDate - The moment date when the plan begins
   * @param rangeDateStr - Date string in 'YYYY-MM' format representing the range start
   * @returns true if plan starts after the range end (warning should be shown)
   */
  function isPlanStartAfterRangeEnd(planStartDate: moment.Moment, rangeDateStr: string): boolean {
    const rangeEndDate = moment(rangeDateStr, 'YYYY-MM').add(1, 'month')
    return planStartDate.isAfter(rangeEndDate)
  }

  /**
   * Determines if a comparison column requires a plan warning.
   * Actual columns never need warnings; plan columns need warnings if plan starts after the range.
   */
  function shouldShowColumnPlanWarning(planStartDate: moment.Moment | undefined, column: { range_date_str?: string; is_actual?: boolean } | undefined, preYear = false): boolean {
    if (!column?.range_date_str) {
      return false
    }

    const columnRangeStart = moment(column.range_date_str.split(',')[0], 'YYYY-MM')

    if (preYear) {
      const actualsStartDate = moment(ctxStore.ref_comp_map[activeCompId.value].ref_comp_actuals_res_values?.meta?.fpna_begin_date_string, 'YYYY-MM')
      return actualsStartDate.isAfter(columnRangeStart)
    }

    if (!column?.range_date_str || column.is_actual || !planStartDate) {
      return false
    }

    return planStartDate.isAfter(columnRangeStart)
  }

  /**
   * Updates planWarning state for the given display mode.
   */
  function updatePlanWarningState(mode: string, shouldWarn: boolean): void {
    planWarning.value = { ...planWarning.value, [mode]: shouldWarn }
  }

  // Watch for changes that affect plan warning visibility
  // Shows warning when selected plan's start date is after the displayed date range
  watch(
    [() => selectedPlan.value, () => lastActualsDate.value, () => route.params, () => activeCompId.value, () => financialsDiff.value],
    ([planMap, actualsDateMap, params, compId, comparisonView]) => {
      const mode = isDashboardRoute.value ? 'dashboard' : (params.display_mode as string)

      const currentPlan = planMap[compId]
      const currentActualsDate = actualsDateMap[compId]
      const planStartDate = moment(currentPlan?.fpna_begin_date_string)

      // Handle comparison/diff view: Check both columns for plan availability
      if (mode === 'diff') {
        const col1NeedsWarning = shouldShowColumnPlanWarning(planStartDate, comparisonView?.col_1)
        const col2NeedsWarning = shouldShowColumnPlanWarning(planStartDate, comparisonView?.col_2, comparisonView?.col_2?.is_actual)
        updatePlanWarningState('comparison', col1NeedsWarning || col2NeedsWarning)
      }

      // Early exit: No plan selected or missing required data
      const hasRequiredData = compId && currentPlan?.fpna_begin_date_string && currentActualsDate
      if (!hasRequiredData) {
        updatePlanWarningState(mode, false)
        return
      }

      // if (mode === 'dashboard') {
      //   if (route.params?.period === 'ytd') {
      //     const { ytd } = calculateYTDAndYTG(currentActualsDate)
      //     const ytdMoment = moment(ytd[0], 'YYYY-MM')
      //     updatePlanWarningState(mode, planStartDate.isAfter(ytdMoment))
      //   } else {
      //     updatePlanWarningState(mode, planStartDate.isAfter(moment(currentActualsDate, 'YYYY-MM')))
      //   }

      //   return
      // }

      // Handle standard views: Check if plan starts after current actuals period
      const shouldWarn = isPlanStartAfterRangeEnd(planStartDate, currentActualsDate)
      updatePlanWarningState(mode, shouldWarn)
    },
    { immediate: true, deep: true },
  )

  // Load FSD and company data when active organization or company changes
  watch(
    [activeCompId, activeOrgId, selectedPlan],
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Centralized data loading logic
    async ([compId, orgId, plan], _oldValues, onCleanup) => {
      let isCancelled = false

      onCleanup(() => {
        isCancelled = true
      })

      if (compId && orgId) {
        const isOnboardingRoute = router.currentRoute.value.path.includes('/onboarding')
        if (isOnboardingRoute) {
          consola.debug('🟡 sharedscope.store: Skipping automated FSD/ACM load - managed by onboarding or data management pages.')
          return
        }

        consola.info(`sharedscope.store: Front-loading all data for orgId: ${orgId}, compId: ${compId}`)

        // Force has_data to false immediately.
        // This puts the UI into "Loading..." state and prevents it from accessing FSD before it's ready.
        if (ctxStore.ref_comp_map[compId]) {
          ctxStore.ref_comp_map[compId].has_data = false
        }

        try {
          // 2. Load FSD (Await this to ensure FSD is ready before we proceed)
          const existingFsdData = ctxStore.ref_fsd_map[orgId]

          if (existingFsdData) {
            fsdStore.setFSDStatementLists(existingFsdData)
            consola.debug('🔵 FSD data already present, fsd.store synced.')
          } else {
            const fsdData = await firstValueFrom(ctxStore.loadFlatFSD(orgId))
            if (isCancelled) return
            if (fsdData) {
              fsdStore.setFSDStatementLists(fsdData)
              consola.debug('✅ FSD data loaded and fsd.store populated.')
            }
          }

          const isDataManagementRoute = router.currentRoute.value.path.includes('/data-management') && !router.currentRoute.value.path.includes('/mapping')
          if (isDataManagementRoute) {
            return
          }

          const existingCompData = ctxStore.ref_comp_map[compId]
          if (existingCompData?.ref_comp_acc_mapping) {
            useAcmStore().loadAccMappingData(existingCompData.ref_comp_acc_mapping)
            consola.debug('🔵 ACM data already present, acm.store synced.')
          }

          // 3. Load company-specific data
          // Now that FSD is ready, we trigger the company data load.
          // changeCompById should eventually set has_data = true when it finishes.

          // Note: Even if data was "already loaded" in the background, we forced has_data = false above,
          // so we must call changeCompById to refresh/reset the state correctly.
          ctxStore.changeCompById(compId)
          consola.debug('⏳ Triggered loading of company-specific actuals.')

          // 4. Proactively kick off KPI *cfg calc* for the new/active comp as soon as we start loading it.
          // On a freshly created org the dashboard (and useKpiLoader) will request res values, but the
          // backend requires the cfg to have been calculated first (cmwCompKpiCfgCalcRequest). Without it
          // the res endpoint returns errors/empty (historically with planId "0"). We fire the cfg path here
          // (with whatever plan is currently selected, or none) so the required RPC happens early in the
          // org activation flow, before or in parallel with the first dashboard render.
          // The useKpiLoader watcher will also drive this (now unconditionally for cfg once has_data is true),
          // but an explicit early call makes the "right after org creation" guarantee stronger and survives
          // route timing.
          const planForKpi = getSelectPlanId(compId)
          const lastDtForKpi = lastActualsDate.value[compId]
          ctxStore.loadKpiResValues(orgId, compId, planForKpi || undefined, lastDtForKpi, true /* isCfg */).subscribe({
            error: (e) => consola.warn('🟡 Early KPI cfg calc (on comp front-load) failed (may retry via watcher):', e),
          })
        } catch (error) {
          consola.error('🔴 Failed to front-load all company data:', error)
        }
      }

      // Skip loadPlanResValues on the planning section — planningview.store handles its own
      // data loading via getPlanConfig (comp_plan_cfg_get) with LRU caching, avoiding duplicate API calls.
      if (compId && orgId && plan[compId] && ctxStore.active_section !== 'plan') {
        ctxStore.loadPlanResValues(compId, plan[compId]?.id, orgId).subscribe()
      }
    },
    { immediate: true, deep: true },
  )

  // Initialize KPI loader composable
  const { initKpiLoadWatcher } = useKpiLoader({
    activeCompId: () => activeCompId.value,
    activeOrgId: () => activeOrgId.value,
    selectedPlan: () => selectedPlan.value,
    lastActualsDate: () => lastActualsDate.value,
  })
  initKpiLoadWatcher()

  // Also expose a tiny helper so other early-load paths (e.g. the explicit one we added above)
  // can stay consistent with how the rest of the app resolves "current plan for this comp".
  // (getSelectPlanId is already returned; this is just a note for future readers.)

  // Watch for changes in Range Date String
  watch(
    () => {
      const section = ctxStore.active_section
      if (!(activeCompId.value && section && ctxStore.ref_comp_map[activeCompId.value])) return undefined
      return ctxStore.ref_comp_map[activeCompId.value].ui_map[section]?.scope_params?.range_date_str
    },
    (newRangeStr) => {
      if (newRangeStr) {
        const [startStr, endStr] = newRangeStr.split(',')
        if (startStr && endStr) {
          const fromDate = new Date(startStr)
          const toDate = new Date(endStr)
          selDateRangeStrategy.value = new CustomDateRangeStrategy({ fromDate, toDate })
        }
      }
    },
    { immediate: true, deep: true },
  )

  // RF Plan Data Loader
  watchEffect((onInvalidate) => {
    const compIdValue = activeCompId.value
    if (!compIdValue) return

    const planId = getSelectPlanId(compIdValue)
    const lastActual = lastActualsDate.value[compIdValue]
    const orgId = ctxStore.active_org_id
    let subscription: Subscription | null = null

    if (planId && lastActual && compIdValue && orgId) {
      consola.debug(`sharedscope.store: Authoritative trigger for RF data load for compId: ${compIdValue}, planId: ${planId}, lastActual: ${lastActual}`)
      subscription = ctxStore.loadRFPlanResValues(compIdValue, planId, orgId, lastActual).subscribe({
        error: (err) => consola.error('🔴 Authoritative RF fetch failed:', err),
      })
    }

    onInvalidate(() => {
      subscription?.unsubscribe()
    })
  })

  // SWR: Background revalidation on section/route change.
  // Shows cached data immediately while fetching fresh data in the background.
  // Triggered by router.plugin.ts setting active_section on every navigation.
  // Only runs on data-heavy sections; skips settings and data-management.
  const swrEnabledSections: SectionKey[] = ['dashboard', 'financials', 'analysis', 'plan']
  watch(
    () => ctxStore.active_section,
    (section) => {
      if (swrEnabledSections.includes(section)) {
        ctxStore.revalidateSection()
      }
    },
  )

  function performParamValidation(actualsStr: string | undefined, periodBegin: Date, periodEnd: Date, section: SectionKey): { actuals: { month: number; year: number }; range: { month: number; year: number }[] } {
    const compId = activeCompId.value
    if (!ctxStore.ref_comp_map[compId]) return { actuals: { month: 0, year: 0 }, range: [] }
    const scopeParams = ctxStore.ref_comp_map[compId].ui_map[section].scope_params
    const actuals = verifyAndGetActualsUrlParam(actualsStr, periodBegin, periodEnd)
    const actualsParamStr = getActualsUrlParamStr(actuals)
    const maxRangeDate = new Date(actuals.year, actuals.month).getTime() < maxDate.value.getTime() ? new Date(actuals.year, actuals.month) : maxDate.value

    if (!scopeParams.range_date_str || scopeParams.range_date_str.split(',').length !== 2) {
      const ytdStrategy = new YearToDateStrategy(new Date(actuals.year, actuals.month, 1), getCompEndMonth() || 12)
      const defaultRange = [
        { month: ytdStrategy.dateRange.fromDate.getMonth(), year: ytdStrategy.dateRange.fromDate.getFullYear() },
        { month: ytdStrategy.dateRange.toDate.getMonth(), year: ytdStrategy.dateRange.toDate.getFullYear() },
      ]
      scopeParams.range_date_str = getRangeUrlParamStr(defaultRange)
    }

    const range = verifyAndGetRangeUrlParam(scopeParams.range_date_str, minDate.value, maxRangeDate)
    const rangeParamStr = getRangeUrlParamStr(range)
    scopeParams.actuals_date_str = actualsParamStr
    scopeParams.range_date_str = rangeParamStr
    consola.debug(`🔵 shared-scope-logic(${section}).performParamValidation: `, compId, scopeParams)
    return { actuals, range }
  }

  function initActualsBoundaries(actuals_date_str: string, section: SectionKey) {
    const compId = activeCompId.value
    const compData = ctxStore.ref_comp_map[compId]
    if (!compData) {
      throw new Error(`Company data not found for company ID: ${compId}`)
    }
    watch(
      () => ctxStore.ref_comp_map[compId].ref_comp_actuals_res_values,
      (ref_actuals_res_values) => {
        const { fpna_begin_date_string: startDate, fpna_end_date_string: endDate } = ref_actuals_res_values?.meta || {}
        if (startDate && endDate) {
          const periodBegin = new Date(startDate)
          const periodEnd = new Date(endDate)
          minDate.value = periodBegin
          maxDate.value = periodEnd
          // Use the live lastActualsDate from the store instead of the stale captured parameter,
          // so that if the user changed last actual on another section, we don't overwrite with old values
          const liveActualsDateStr = lastActualsDate.value[compId] || actuals_date_str
          performParamValidation(liveActualsDateStr, periodBegin, periodEnd, section)
        }
      },
      { immediate: true },
    )
  }

  function validateAndUpdateScopeParams(selDate: { month: number; year: number }, section: SectionKey, selectedView?: Ref<FinancialGridDataView>) {
    const compId = activeCompId.value
    const { actuals } = performParamValidation(getDateStr(selDate), minDate.value, maxDate.value, section)
    const currentScopeParams = ctxStore.ref_comp_map[compId].ui_map[section].scope_params
    ctxStore.ref_comp_map[compId].ui_map[section].scope_params = { ...currentScopeParams }

    if (selectedView?.value === FinancialGridDataView.MonthlyView) {
      setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent('monthly-scope-params-updated', { detail: { compId, newDate: selDate } }))
        } catch (error) {
          consola.error('monthly-fix: shared-scope-logic - error dispatching event:', error)
        }
      }, 100)
    }

    // Ensure Strategy Exists
    if (!selDateRangeStrategy.value) {
      selDateRangeStrategy.value = new YearToDateStrategy(new Date(actuals.year, actuals.month, 1), getCompEndMonth() || 12)
    }

    // Update Data
    selDateRangeStrategy.value.updateData(new Date(actuals.year, actuals.month, 1))

    // FIX: Safely access dates to prevent "reading 'getMonth' of undefined"
    const fromDate = selDateRangeStrategy.value?.dateRange?.fromDate
    const toDate = selDateRangeStrategy.value?.dateRange?.toDate

    if (fromDate && toDate) {
      const rangeFromDateRangeStrategy = [
        { month: fromDate.getMonth(), year: fromDate.getFullYear() },
        { month: toDate.getMonth(), year: toDate.getFullYear() },
      ]
      ctxStore.ref_comp_map[compId].ui_map[section].scope_params.range_date_str = getRangeUrlParamStr(rangeFromDateRangeStrategy)
    } else {
      consola.warn('⚠️ validateAndUpdateScopeParams: DateStrategy returned incomplete dates. Skipping range update.', { fromDate, toDate })
    }
  }

  function _setSelectedPlan(plan: FinancialsGridDataAvailablePlanList) {
    const key = activeCompId.value
    selectedPlan.value[key] = plan
  }

  function initializeDefaultDate(key: string) {
    if (userHasSetActualsDate.value[key] || lastActualsDate.value[key]) return
    const defaultDate = ctxStore.ref_comp_map[key]?.ref_comp_actuals_res_values?.meta?.fpna_end_date_string
    if (defaultDate) {
      lastActualsDate.value[key] = defaultDate
    }
  }

  function clearSelectedPlan() {
    const key = activeCompId.value
    delete selectedPlan.value[key]
  }

  /**
   * Clear date-related data for a specific company.
   * Use this when company data is deleted and you need to clear
   * cached date information regardless of whether it's the active company.
   * @param compId - The ID of the company to clear data for
   */
  function clearCompanyDateData(compId: string) {
    delete selectedPlan.value[compId]
    delete lastActualsDate.value[compId]
    delete userHasSetActualsDate.value[compId]
  }

  function _getLastActualsDate() {
    return lastActualsDate.value[activeCompId.value]
  }

  function _setLastActualsDate(_date: string) {
    const key = activeCompId.value
    lastActualsDate.value[key] = _date
    userHasSetActualsDate.value[key] = true
  }

  function _hasUserSetActualsDate() {
    return !!userHasSetActualsDate.value[activeCompId.value]
  }

  async function selectLastActualsDate(dateStr: string) {
    const compId = activeCompId.value
    if (!compId) return
    const section = ctxStore.active_section
    _setLastActualsDate(dateStr)
    await updateLastActualsDate(compId, section, dateStr)
  }

  async function selectPlan(planId: string | null) {
    const compId = activeCompId.value
    if (!compId) return

    const section = ctxStore.active_section
    if (planId) {
      const plan = availablePlans.value.find((p) => p.id === planId)
      if (plan) {
        _setSelectedPlan(plan)
        await updateSelectedPlanId(compId, section, planId)
      }
    } else {
      clearSelectedPlan()
      await updateSelectedPlanId(compId, section, '')
    }
  }

  return {
    activeOrgId,
    activeCompId,
    selectedPlan,
    lastActualsDate,
    userHasSetActualsDate,
    minDate,
    maxDate,
    selDateRangeStrategy,
    availablePlans,
    planWarning,
    hasPlan,
    getSelectPlanId,
    setSelectedPlan: _setSelectedPlan,
    initializeDefaultDate,
    clearSelectedPlan,
    clearCompanyDateData,
    getLastActualsDate: _getLastActualsDate,
    setLastActualsDate: _setLastActualsDate,
    hasUserSetActualsDate: _hasUserSetActualsDate,
    initActualsBoundaries,
    validateAndUpdateScopeParams,
    // Orchestrators
    selectLastActualsDate,
    selectPlan,
  }
})

/**
 * Store for managing UI state of FpnaScopeParams modals.
 */
export const useFpnaScopeStateStore = defineStore('fpna-scope-state', {
  state: () => ({
    planMenuOpen: false,
    isPlanModalOpen: false,
    isEditMode: false,
  }),
  actions: {
    openPlanMenu() {
      this.planMenuOpen = true
    },
    closePlanMenu() {
      this.planMenuOpen = false
    },
    togglePlanMenu() {
      this.planMenuOpen = !this.planMenuOpen
    },
    openAddPlanModal() {
      this.isPlanModalOpen = true
      this.isEditMode = false
      this.planMenuOpen = false
    },
    openEditPlanModal() {
      this.isPlanModalOpen = true
      this.isEditMode = true
      this.planMenuOpen = false
    },
    closePlanModal() {
      this.isPlanModalOpen = false
    },
  },
})
